/**
 * PURPOSE: Detalle de una prueba combinada (`PCB`: decatlón / heptatlón).
 * RESPONSIBILITIES: Lista de sub-pruebas (`combined.events`) con estado y horario — al hacer clic se
 *   abre la sub-prueba con su propia grilla — y la tabla de atletas con puntos WA por prueba, total y
 *   puesto, tal como los devuelve el servidor.
 * COLLABORATORS: PruebaDetailPanel.jsx.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/CombinedPanel.jsx
 */
import React from "react";
import { FiChevronRight, FiClock } from "react-icons/fi";

import { formatShortDate, formatTime, institutionAbrev, participantName } from "../labels.js";
import { FrozenScroll, buildFrozenColumns } from "./grids/gridCells.jsx";
import { EmptyState, EntryStatusChip, PruebaStatusChip } from "./ui.jsx";

const th = "whitespace-nowrap px-2 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500";
const thc = "whitespace-nowrap px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-500";

const frozen = buildFrozenColumns([
  ["place", 4],
  ["athlete", 12],
  ["institution", 5.5],
]);

const CombinedPanel = ({ combined, onOpenEvent }) => {
  const events = [...(Array.isArray(combined?.events) ? combined.events : [])].sort(
    (a, b) => (Number(a.order) || 0) - (Number(b.order) || 0)
  );
  const athletes = Array.isArray(combined?.athletes) ? combined.athletes : [];

  if (!events.length && !athletes.length) {
    return <EmptyState title="Combinada sin pruebas" description="El servidor no devolvió las pruebas de esta combinada." />;
  }

  return (
    <div>
      <div className="border-b border-slate-100 p-4">
        <h4 className="mb-2 text-sm font-black uppercase tracking-wide text-indigo-700">Pruebas de la combinada</h4>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {events.map((event) => (
            <button
              key={event.pruebaId}
              type="button"
              onClick={() => onOpenEvent(event)}
              className="group flex items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2 text-left transition hover:border-indigo-300 hover:bg-indigo-50"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-800">
                  <span className="mr-1 text-slate-400">{event.order}.</span>
                  {event.name || event.key}
                </p>
                <p className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-500">
                  <FiClock aria-hidden="true" />
                  {event.schedule?.date || event.schedule?.time
                    ? `${formatShortDate(event.schedule?.date)} ${formatTime(event.schedule?.time)}`
                    : "Sin horario"}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <PruebaStatusChip status={event.status} />
                <FiChevronRight className="text-slate-400 group-hover:text-indigo-600" aria-hidden="true" />
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="p-4">
        <h4 className="mb-2 text-sm font-black uppercase tracking-wide text-indigo-700">Clasificación de la combinada</h4>
        {!athletes.length ? (
          <p className="text-sm italic text-slate-500">Todavía no hay atletas con puntos.</p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <FrozenScroll>
              <table className="w-full min-w-[720px] table-auto">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th {...frozen("place", thc)}>Puesto</th>
                    <th {...frozen("athlete", th)}>Atleta</th>
                    <th {...frozen("institution", th)}>Univ.</th>
                    {events.map((event) => (
                      <th key={event.pruebaId} className={thc} title={event.name}>
                        {event.key || event.name}
                      </th>
                    ))}
                    <th className={thc}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {athletes.map((athlete, index) => {
                    const perEvent = new Map(
                      (athlete.perEvent || []).map((item) => [Number(item.pruebaId), item])
                    );
                    return (
                      <tr
                        key={`${athlete.participant?.entryId ?? index}`}
                        className="border-b border-slate-100 bg-white last:border-b-0"
                      >
                        <td {...frozen("place", "px-2 py-2 text-center text-sm font-black text-slate-800")}>
                          {athlete.place ?? "—"}
                          {athlete.tied ? <span className="ml-0.5 text-xs text-slate-400">=</span> : null}
                        </td>
                        <td {...frozen("athlete", "px-2 py-2 text-sm font-semibold text-slate-800 [overflow-wrap:anywhere]")}>
                          {participantName(athlete.participant)}
                        </td>
                        <td {...frozen("institution", "px-2 py-2 text-xs font-bold text-slate-600")}>
                          {institutionAbrev(athlete.participant?.institution)}
                        </td>
                        {events.map((event) => {
                          const item = perEvent.get(Number(event.pruebaId));
                          return (
                            <td key={event.pruebaId} className="px-2 py-2 text-center">
                              {item ? (
                                <>
                                  <span className="block text-sm font-bold text-indigo-700">{item.points ?? "—"}</span>
                                  {item.mark ? (
                                    <span className="block font-mono text-[11px] text-slate-500">{item.mark}</span>
                                  ) : item.status && item.status !== "OK" ? (
                                    <EntryStatusChip status={item.status} />
                                  ) : null}
                                </>
                              ) : (
                                <span className="text-xs text-slate-300">—</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-2 py-2 text-center text-base font-black text-slate-900">{athlete.total ?? "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </FrozenScroll>
          </div>
        )}
      </div>
    </div>
  );
};

export default CombinedPanel;
